// ═══════════════════════════════════════════════════════════════════════════
// InvoiceHistory — el libro de documentos
//
// Responde "¿qué emitimos?": todas las remisiones, en orden cronológico,
// cruzando clientes. La otra pregunta —"¿quién me debe?"— la contesta Cuentas,
// que es la misma información ordenada por cliente y con los saldos al frente.
//
// Las acciones sobre una remisión YA NO viven aquí: son las mismas en las tres
// pantallas y viven en `useRemisionAcciones`. Esta pantalla solo decide cómo
// se ENCUENTRA el documento, nunca qué se puede hacer con él.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState } from "react";
import { SAT_FORMA_PAGO, SAT_METODO_PAGO, SAT_MOTIVOS_CANCELACION } from "../fiscal/catalogos-sat.js";
import { fmt } from "../pdf/comunes.js";
import { esperaFolio, estaCancelada } from "../datos/remisiones-pg.js";
import { estadoComision, COMISION } from "../dominio/estado-comision.js";
import { Card, SectionTitle } from "./basicos.jsx";
import { useRemisionAcciones } from "./RemisionAcciones.jsx";

export function InvoiceHistory({ invoices, setInvoices, products, clients,
                                 onAbrirEnEditor, membresia, onIrAConfig }) {
  const [expandedId, setExpandedId] = useState(null);
  const { barraDe, modales } = useRemisionAcciones({
    clients, products, invoices, setInvoices, membresia, onAbrirEnEditor, onIrAConfig });

  return (
    <div>
      <SectionTitle sub={`${invoices.length} remisiones guardadas`}>Historial</SectionTitle>
      {invoices.length === 0 ? (
        <Card><p style={{ color: "#555", textAlign: "center", padding: 40 }}>No hay remisiones guardadas aún. Crea una en la pestaña "Nueva Remisión".</p></Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[...invoices].sort((a, b) => new Date(b.date) - new Date(a.date)).map((inv, idx) => (
            <Card key={inv.id || `inv-${idx}`} style={{ cursor: "pointer" }} onClick={() => setExpandedId(expandedId === inv.id ? null : inv.id)}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div>
                    <span style={{ color: "#c9a96e", fontWeight: 600, marginRight: 12 }}>{esperaFolio(inv) ? "PENDIENTE" : `#${inv.folio}`}</span>
                    <span style={{ fontSize: 14 }}>{inv.client.name}</span>
                  </div>
                  {inv.cfdiId ? (
                    inv.cfdiStatus === "canceled" ? (
                      <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 4, background: "#1f0d0d", color: "#f87171", border: "1px solid #3a1a1a", letterSpacing: 1 }}>
                        CANCELADO ✗
                      </span>
                    ) : (
                      <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 4, background: "#0d1f0d", color: "#4ade80", border: "1px solid #1a3a1a", letterSpacing: 1 }}>
                        CFDI ✓
                      </span>
                    )
                  ) : (() => {
                    const cc = clients.find(c => c.id === inv.client.id) || inv.client;
                    const hasCsf = cc.rfc && cc.taxSystem && cc.domicilioFiscalZip && cc.legalName;
                    return hasCsf ? (
                      <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 4, background: "#1f1a0d", color: "#facc15", border: "1px solid #3a331a", letterSpacing: 1 }}>
                        CSF ✓
                      </span>
                    ) : (
                      <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 4, background: "#111", color: "#555", border: "1px solid #222", letterSpacing: 1 }}>
                        Sin CSF
                      </span>
                    );
                  })()}
                  {/* El estado de la comisión, a la vista. El operador:
                      "siempre debe saber cuál es el estado de cualquiera". */}
                  {(() => {
                    const e = estadoComision(inv);
                    if (e.clave === COMISION.sin_comision.clave) return null;
                    return (
                      <span title={`${e.ayuda} · ${fmt(inv.comision)}`}
                        style={{ fontSize: 9, padding: "2px 8px", borderRadius: 4,
                                 background: "#111", color: e.color,
                                 border: `1px solid ${e.color}33`, letterSpacing: 1 }}>
                        {e.etiqueta.toUpperCase()} {fmt(inv.comision)}
                      </span>
                    );
                  })()}
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "#c9a96e" }}>{fmt(inv.total)}</div>
                  <div style={{ fontSize: 11, color: "#555" }}>
                    {new Date(inv.date).toLocaleDateString("es-MX")} · {inv.totalBottles} bot.
                  </div>
                </div>
              </div>
              {expandedId === inv.id && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #222" }} onClick={e => e.stopPropagation()}>
                  {inv.lines.map((li, i) => {
                    const isCortesia = (li.unitPrice || 0) === 0;
                    return (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 13 }}>
                      <span style={{ color: "#999" }}>{li.qty}x {li.productName}</span>
                      <span style={{ color: isCortesia ? "#70c9c9" : "#888" }}>{isCortesia ? "cortesía" : `${fmt(li.unitPrice)} c/u → ${fmt(li.amount)}`}</span>
                    </div>
                    );
                  })}
                  {/* Totals breakdown */}
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #222", maxWidth: 300, marginLeft: "auto" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#888", padding: "2px 0" }}>
                      <span>Importe bruto ({inv.totalBottles} bot.)</span><span>{fmt(inv.subtotalBeforeDiscount)}</span>
                    </div>
                    {inv.discount > 0 && <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#c97070", padding: "2px 0" }}>
                      <span>Descuento ({inv.discount}%)</span><span>-{fmt(inv.discountAmount)}</span>
                    </div>}
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#888", padding: "2px 0" }}>
                      <span>Base gravable</span><span>{fmt(inv.subtotal)}</span>
                    </div>
                    {(inv.iepsBreakdown || []).map((b, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#888", padding: "2px 0" }}>
                        <span>IEPS ({b.rate}%)</span><span>{fmt(b.ieps)}</span>
                      </div>
                    ))}
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#888", padding: "2px 0" }}>
                      <span>IVA ({inv.ivaRate}%)</span><span>{fmt(inv.ivaAmount)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 600, color: "#c9a96e", borderTop: "1px solid #333", marginTop: 4, paddingTop: 6 }}>
                      <span>TOTAL NETO</span><span>{fmt(inv.total)}</span>
                    </div>
                    {inv.cfdiTotal && Math.abs(inv.cfdiTotal - inv.total) > 0.01 && (
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#4ade80", padding: "2px 0" }}>
                        <span>Total CFDI (oficial)</span><span>{fmt(inv.cfdiTotal)}</span>
                      </div>
                    )}
                    <div style={{ textAlign: "right", fontSize: 11, color: "#888", marginTop: 2 }}>
                      Total botellas: {inv.totalBottles}
                    </div>
                  </div>

                  <div style={{ marginTop: 8, fontSize: 11, color: "#555" }}>
                    Pago: {SAT_FORMA_PAGO.find(f => f.code === inv.formaPago)?.label || inv.formaPago || "—"} · {SAT_METODO_PAGO.find(m => m.code === inv.metodoPago)?.label || inv.metodoPago || "—"}
                  </div>
                  {inv.notes && <div style={{ marginTop: 6, fontSize: 12, color: "#888", fontStyle: "italic" }}>{inv.notes}</div>}

                  {/* CFDI info block */}
                  {inv.cfdiId && (
                    <div style={{ marginTop: 12, padding: 12, background: inv.cfdiStatus === "canceled" ? "#1f0d0d" : "#0d1f0d", borderRadius: 8, border: `1px solid ${inv.cfdiStatus === "canceled" ? "#3a1a1a" : "#1a3a1a"}` }}>
                      <div style={{ fontSize: 11, color: inv.cfdiStatus === "canceled" ? "#f87171" : "#4ade80", letterSpacing: 2, marginBottom: 6 }}>
                        {inv.cfdiStatus === "canceled" ? "CFDI CANCELADO" : "CFDI TIMBRADO"}
                      </div>
                      <div style={{ fontSize: 12, color: "#888" }}>
                        <div>UUID: <span style={{ fontFamily: "monospace", color: "#e8e0d4" }}>{inv.cfdiUuid || inv.cfdiId}</span></div>
                        <div>Estatus: <span style={{ color: inv.cfdiStatus === "canceled" ? "#f87171" : "#4ade80" }}>{inv.cfdiStatus || "valid"}</span></div>
                        {inv.cfdiTotal && <div>Total CFDI: <span style={{ color: "#e8e0d4" }}>{fmt(inv.cfdiTotal)}</span></div>}
                        {inv.cancelMotive && <div>Motivo cancelación: <span style={{ color: "#e8e0d4" }}>{inv.cancelMotive} — {SAT_MOTIVOS_CANCELACION.find(m => m.code === inv.cancelMotive)?.label || ""}</span></div>}
                        {inv.cfdiVerificationUrl && (
                          <div style={{ marginTop: 4 }}>
                            <a href={inv.cfdiVerificationUrl} target="_blank" rel="noopener" style={{ color: "#c9a96e", fontSize: 11 }}>
                              🔗 Verificar en SAT
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Mismas acciones que en Cuentas y en la ficha del cliente.
                      Las decide el estado del documento, no esta pantalla. */}
                  <div style={{ marginTop: 12 }}>
                    {barraDe(inv)}
                    {estaCancelada(inv) && (
                      <div title={`Cancelada el ${new Date(inv.canceledAt).toLocaleString("es-MX")}`}
                        style={{ marginTop: 8, fontSize: 11, padding: "6px 12px", borderRadius: 4,
                          background: "#1f0d0d", color: "#c97070", textAlign: "right" }}>
                        🚫 Cancelada — {inv.cancelReason}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {modales}

    </div>
  );
}
